<template>
  <div
    class="relative flex flex-row rounded-md shadow-sm focus-within:border-primary-400 focus-within:ring-0"
    :class="{
      'mt-2': !!label,
      'bg-white': !disabled,
      'bg-gray-100': disabled
    }"
  >
    <BaseLabelTop
      :target="'name'"
      :label="label"
      :disabled="disabled"
      :required="required"
      :label-class="labelClass"
    />
    <div
      class="flex w-full flex-row overflow-hidden rounded-md border shadow-sm"
      :class="{
        'bg-white': !disabled,
        'bg-gray-100': disabled,
        [selectClass]: !!selectClass
      }"
    >
      <Listbox v-model="selectedValues" multiple :disabled="disabled">
        <Float
          adaptive-width
          portal
          strategy="fixed"
          placement="bottom-start"
          :flip="true"
          :shift="true"
          :offset="2"
        >
          <ListboxButton
            class="flex min-w-0 flex-1 flex-row border-0 p-0 px-3 text-gray-900 placeholder-gray-500 outline-0 sm:text-sm dark:border-white dark:bg-slate-800 dark:text-gray-300"
            :class="sizeClass"
          >
            <span class="block text-left break-words">
              {{ selectedValuesLabel }}
            </span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <FontAwesomeIcon :icon="['fal', 'chevron-down']" />
            </span>
          </ListboxButton>
          <ListboxOptions class="listbox-options">
            <div v-if="includeSelectAllOption" class="flex flex-row p-2 pt-1">
              <button
                type="button"
                class="flex-1 rounded-l-md border border-r-0 border-l bg-white py-1 text-center hover:bg-primary-500 dark:bg-slate-800"
                tabindex="-1"
                @click="selectAll"
              >
                {{ $t('global.all') }}
              </button>
              <button
                type="button"
                class="flex-1 rounded-r-md border border-r bg-white py-1 text-center hover:bg-primary-500 dark:bg-slate-800"
                tabindex="-1"
                @click="unselectAll"
              >
                {{ $t('global.none') }}
              </button>
            </div>
            <ListboxOption
              v-for="value in orderedValues"
              v-slot="{ active, selected }"
              :key="value"
              :value="value[trackBy]"
              as="template"
            >
              <li :aria-disabled="disabled">
                <slot
                  name="option"
                  :option="value"
                  :active="active"
                  :selected="selected"
                >
                  <span
                    :class="[
                      selected ? 'font-medium' : 'font-normal',
                      'block break-words'
                    ]"
                  >
                    {{ value[displayKey] }}
                  </span>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                  </span>
                </slot>
                <span
                  v-if="favorite !== null && favorite.includes(value[trackBy])"
                  :class="[
                    'absolute inset-y-0 right-0 flex items-center pr-4 text-yellow-300'
                  ]"
                >
                  <FontAwesomeIcon :icon="['fas', 'star-of-life']" />
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Float>
      </Listbox>
    </div>
  </div>
  <ErrorContainer :error-key="name" :errors="errors" />
</template>

<script>
import { computed } from 'vue'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Float } from '@headlessui-float/vue'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'

export default {
  name: 'MultiSelectComponent',
  components: {
    BaseLabelTop,
    ErrorContainer,
    Float,
    FontAwesomeIcon,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption
  },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'normal'
    },
    values: {
      type: Array,
      required: true
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    trackBy: {
      type: String,
      required: false,
      default: 'id'
    },
    displayKey: {
      type: String,
      required: false,
      default: 'name'
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    selectedLabel: {
      type: String,
      required: false,
      default: null
    },
    noneSelectedLabel: {
      type: String,
      required: false,
      default: null
    },
    numberDisplayed: {
      type: Number,
      required: false,
      default: 1
    },
    includeSelectAllOption: {
      type: Boolean,
      required: false,
      default: true
    },
    favorite: {
      type: Array,
      required: false,
      default: null
    },
    favoriteOnTop: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Array,
      required: false,
      default: () => []
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    capitalize: {
      type: Boolean,
      required: false,
      default: true
    },
    labelClass: {
      type: String,
      required: false,
      default: ''
    },
    selectClass: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const selectedValues = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    return { selectedValues }
  },
  computed: {
    sizeClass() {
      switch (this.size) {
        case 'small':
          return 'py-1.5'
        case 'normal':
          return 'py-2'
      }
    },
    orderedValues() {
      if (
        this.favorite === null ||
        !Array.isArray(this.favorite) ||
        this.favorite.length === 0 ||
        !this.favoriteOnTop
      ) {
        return this.values
      }
      return this.values.sort((a, b) =>
        this.favorite.includes(a.id) ? -1 : this.favorite.includes(b.id) ? 1 : 0
      )
    },
    selectedValuesLabel() {
      if (!this.selectedValues) {
        return this.noneSelectedLabel ?? this.$t('global.aucun_selectionne')
      }
      let lSelected = this.selectedValues.length
      if (lSelected > this.numberDisplayed) {
        return (
          lSelected +
          ' ' +
          (this.selectedLabel ?? this.$t('global.selectionnes'))
        )
      } else if (lSelected === 0) {
        return this.noneSelectedLabel ?? this.$t('global.aucun_selectionne')
      } else if (
        lSelected <= this.numberDisplayed &&
        this.selectedValues.length > 0
      ) {
        return this.values
          .filter((value) => this.modelValue.includes(value[this.trackBy]))
          .map((value) => value[this.displayKey])
          .join(', ')
      }
    }
  },
  methods: {
    selectAll() {
      this.selectedValues = this.values.map((value) => value[this.trackBy])
    },
    unselectAll() {
      this.selectedValues = []
    }
  }
}
</script>

<style scoped></style>
